import React, { useState } from 'react';
import { 
  FaUpload, FaTrash, FaPlus, FaDownload, FaCheck, FaTimes, 
  FaUsers, FaDollarSign, FaSearch, FaCheckCircle, FaTimesCircle 
} from 'react-icons/fa';
import { FiFileText, FiUserCheck, FiUserX } from 'react-icons/fi';
import './BulkPayout.css';

interface Payment {
  id: string;
  recipient_contact: string;
  amount: number;
  note: string;
  status?: 'pending' | 'verified' | 'invalid';
  recipient_name?: string;
  recipient_email?: string;
  error?: string;
}

interface LookupResult {
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  is_active: boolean;
}

interface BulkPayoutResult {
  summary: {
    total_successful: number;
    total_failed: number;
    total_amount: number;
    currency: string;
    new_balance_usd: number;
    new_balance_lrd: number;
  };
  successful_transactions: Array<{
    recipient_contact: string;
    recipient_name: string;
    amount: number;
    reference: string;
  }>;
  failed_transactions: Array<{
    recipient_contact: string;
    amount: number;
    error: string;
  }>;
}

const API_URL = process.env.REACT_APP_API_URL || 'https://api.liberianpost.com';

const BulkPayout: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [currency, setCurrency] = useState<'USD' | 'LRD'>('USD');
  const [description, setDescription] = useState('');
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<BulkPayoutResult | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [lookupLoading, setLookupLoading] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ show: boolean; message: string; type: string }>({
    show: false,
    message: '',
    type: ''
  });

  const token = localStorage.getItem('merchant_token');

  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  // Lookup recipient before adding
  const handleLookupAndAdd = async (contact: string, amount?: number, note?: string) => {
    if (!contact) {
      showNotification('Please enter recipient contact', 'error');
      return;
    }

    setLookupLoading(contact);

    try {
      const response = await fetch(`${API_URL}/merchants/wallet/lookup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ contact })
      });

      const data = await response.json();

      if (data.success) {
        const newPayment: Payment = {
          id: Date.now().toString(),
          recipient_contact: contact,
          amount: amount || 0,
          note: note || '',
          status: 'verified',
          recipient_name: `${data.data.first_name} ${data.data.last_name}`,
          recipient_email: data.data.email
        };
        setPayments([...payments, newPayment]);
        showNotification(`✓ ${data.data.first_name} ${data.data.last_name} verified`, 'success');
      } else {
        const failedPayment: Payment = {
          id: Date.now().toString(),
          recipient_contact: contact,
          amount: amount || 0,
          note: note || '',
          status: 'invalid',
          error: data.error || 'User not found'
        };
        setPayments([...payments, failedPayment]);
        showNotification(`✗ ${contact} - ${data.error || 'User not found'}`, 'error');
      }
    } catch (error) {
      console.error('Lookup error:', error);
      const failedPayment: Payment = {
        id: Date.now().toString(),
        recipient_contact: contact,
        amount: amount || 0,
        note: note || '',
        status: 'invalid',
        error: 'Failed to verify recipient'
      };
      setPayments([...payments, failedPayment]);
      showNotification(`✗ ${contact} - Verification failed`, 'error');
    } finally {
      setLookupLoading(null);
    }
  };

  // Add empty payment row (will need lookup)
  const addPayment = () => {
    const newPayment: Payment = {
      id: Date.now().toString(),
      recipient_contact: '',
      amount: 0,
      note: '',
      status: 'pending'
    };
    setPayments([...payments, newPayment]);
  };

  const updatePayment = (id: string, field: keyof Payment, value: string | number) => {
    setPayments(payments.map(p => 
      p.id === id ? { ...p, [field]: value, status: 'pending', recipient_name: undefined, error: undefined } : p
    ));
  };

  const verifyPayment = (id: string) => {
    const payment = payments.find(p => p.id === id);
    if (payment && payment.recipient_contact) {
      handleLookupAndAdd(payment.recipient_contact, payment.amount, payment.note);
      // Remove the old pending entry
      setPayments(payments.filter(p => p.id !== id));
    } else {
      showNotification('Please enter recipient contact first', 'error');
    }
  };

  const removePayment = (id: string) => {
    setPayments(payments.filter(p => p.id !== id));
  };

  const clearAll = () => {
    if (window.confirm('Are you sure you want to clear all payments?')) {
      setPayments([]);
      setResult(null);
    }
  };

  const handleBulkPayout = async () => {
    const verifiedPayments = payments.filter(p => p.status === 'verified' && p.recipient_contact && p.amount > 0);
    
    if (verifiedPayments.length === 0) {
      showNotification('No verified payments to process. Please verify recipients first.', 'error');
      return;
    }

    setProcessing(true);
    setResult(null);

    try {
      const response = await fetch(`${API_URL}/merchants/wallet/bulk-payout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          payments: verifiedPayments.map(p => ({
            recipient_contact: p.recipient_contact,
            amount: p.amount,
            note: p.note || description || 'Salary payment'
          })),
          currency,
          description,
          purpose: 'Bulk salary payment'
        })
      });

      const data = await response.json();

      if (data.success) {
        setResult(data.data);
        showNotification(data.message, 'success');
        // Remove successfully processed payments
        setPayments(payments.filter(p => p.status !== 'verified'));
        setShowPreview(false);
      } else {
        showNotification(data.error || 'Bulk payout failed', 'error');
      }
    } catch (error) {
      console.error('Bulk payout error:', error);
      showNotification('Failed to process bulk payout', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = "recipient_contact,amount,note\nemployee1@company.com,100,Salary Jan\nemployee2@company.com,150,Salary Jan\n23123456789,200,Bonus";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'salary_payout_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const content = event.target?.result as string;
      const lines = content.split('\n');
      
      const newPayments: Payment[] = [];
      
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        
        const values = lines[i].split(',');
        if (values.length >= 2) {
          const contact = values[0].trim();
          const amount = parseFloat(values[1]) || 0;
          const note = values[2]?.trim() || '';
          
          // Auto-verify each contact from CSV
          try {
            const response = await fetch(`${API_URL}/merchants/wallet/lookup`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ contact })
            });
            const data = await response.json();
            
            if (data.success) {
              newPayments.push({
                id: Date.now().toString() + i,
                recipient_contact: contact,
                amount,
                note,
                status: 'verified',
                recipient_name: `${data.data.first_name} ${data.data.last_name}`,
                recipient_email: data.data.email
              });
            } else {
              newPayments.push({
                id: Date.now().toString() + i,
                recipient_contact: contact,
                amount,
                note,
                status: 'invalid',
                error: data.error || 'User not found'
              });
            }
          } catch (err) {
            newPayments.push({
              id: Date.now().toString() + i,
              recipient_contact: contact,
              amount,
              note,
              status: 'invalid',
              error: 'Verification failed'
            });
          }
        }
      }
      
      setPayments([...payments, ...newPayments]);
      const verifiedCount = newPayments.filter(p => p.status === 'verified').length;
      showNotification(`Added ${verifiedCount} verified payments from file`, 'success');
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const totalAmount = payments.filter(p => p.status === 'verified').reduce((sum, p) => sum + (p.amount || 0), 0);
  const verifiedCount = payments.filter(p => p.status === 'verified').length;
  const invalidCount = payments.filter(p => p.status === 'invalid').length;
  const pendingCount = payments.filter(p => p.status === 'pending').length;

  return (
    <div className="bulk-payout">
      {notification.show && (
        <div className={`bulk-notification ${notification.type}`}>
          <span>{notification.message}</span>
        </div>
      )}

      <div className="bulk-header">
        <h2>
          <FaUsers /> Bulk Salary Payout
        </h2>
        <div className="bulk-actions">
          <button onClick={downloadTemplate} className="template-btn">
            <FaDownload /> Download Template
          </button>
          <label className="upload-btn">
            <FaUpload /> Upload CSV
            <input type="file" accept=".csv" onChange={handleFileUpload} hidden />
          </label>
        </div>
      </div>

      <div className="bulk-controls">
        <div className="currency-selector">
          <label>Currency:</label>
          <button className={currency === 'USD' ? 'active' : ''} onClick={() => setCurrency('USD')}>
            USD
          </button>
          <button className={currency === 'LRD' ? 'active' : ''} onClick={() => setCurrency('LRD')}>
            LRD
          </button>
        </div>
        <div className="description-input">
          <input
            type="text"
            placeholder="Description (e.g., January Salary)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="summary-info">
          <span className="verified-count">✅ {verifiedCount} Verified</span>
          {invalidCount > 0 && <span className="invalid-count">❌ {invalidCount} Invalid</span>}
          {pendingCount > 0 && <span className="pending-count">⏳ {pendingCount} Pending</span>}
          <span>💰 Total: {currency === 'USD' ? '$' : 'L$'}{totalAmount.toLocaleString()}</span>
        </div>
      </div>

      <div className="payments-table-container">
        <table className="payments-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Recipient (Email or Phone)</th>
              <th>Amount ({currency})</th>
              <th>Note</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {payments.length === 0 ? (
              <tr>
                <td colSpan={6} className="empty-row">
                  <div className="empty-state">
                    <FiFileText size={48} />
                    <p>No payments added yet</p>
                    <button onClick={addPayment} className="add-first-btn">
                      <FaPlus /> Add First Payment
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              payments.map((payment, index) => (
                <tr key={payment.id} className={`status-${payment.status}`}>
                  <td>{index + 1}</td>
                  <td>
                    <div className="recipient-cell">
                      <input
                        type="text"
                        value={payment.recipient_contact}
                        onChange={(e) => updatePayment(payment.id, 'recipient_contact', e.target.value)}
                        placeholder="email@example.com or phone"
                        className="recipient-input"
                        disabled={payment.status === 'verified'}
                      />
                      {payment.recipient_name && (
                        <div className="recipient-name">
                          <FiUserCheck /> {payment.recipient_name}
                        </div>
                      )}
                      {payment.error && (
                        <div className="recipient-error">
                          <FiUserX /> {payment.error}
                        </div>
                      )}
                    </div>
                   </td>
                   <td>
                    <input
                      type="number"
                      value={payment.amount || ''}
                      onChange={(e) => updatePayment(payment.id, 'amount', parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      className="amount-input"
                      step="0.01"
                      disabled={payment.status === 'verified'}
                    />
                   </td>
                   <td>
                    <input
                      type="text"
                      value={payment.note}
                      onChange={(e) => updatePayment(payment.id, 'note', e.target.value)}
                      placeholder="Optional note"
                      className="note-input"
                      disabled={payment.status === 'verified'}
                    />
                   </td>
                   <td>
                    {payment.status === 'pending' && (
                      <button 
                        onClick={() => verifyPayment(payment.id)} 
                        className="verify-btn"
                        disabled={lookupLoading === payment.recipient_contact}
                      >
                        {lookupLoading === payment.recipient_contact ? '...' : <><FaSearch /> Verify</>}
                      </button>
                    )}
                    {payment.status === 'verified' && (
                      <span className="status-badge verified">
                        <FaCheckCircle /> Verified
                      </span>
                    )}
                    {payment.status === 'invalid' && (
                      <span className="status-badge invalid">
                        <FaTimesCircle /> Invalid
                      </span>
                    )}
                   </td>
                   <td>
                    <button onClick={() => removePayment(payment.id)} className="remove-btn">
                      <FaTrash />
                    </button>
                   </td>
                 </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {payments.length > 0 && (
        <div className="bulk-footer">
          <button onClick={addPayment} className="add-payment-btn">
            <FaPlus /> Add Another
          </button>
          <button onClick={clearAll} className="clear-btn">
            <FaTrash /> Clear All
          </button>
          <button 
            onClick={() => setShowPreview(true)} 
            className="preview-btn"
            disabled={verifiedCount === 0}
          >
            Preview & Send ({verifiedCount} verified)
          </button>
        </div>
      )}

      {showPreview && (
        <div className="preview-modal-overlay" onClick={() => setShowPreview(false)}>
          <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Preview Bulk Payout</h3>
            <div className="preview-summary">
              <div className="preview-item">
                <span>Verified Recipients:</span>
                <strong>{verifiedCount}</strong>
              </div>
              <div className="preview-item">
                <span>Total Amount:</span>
                <strong>{currency === 'USD' ? '$' : 'L$'}{totalAmount.toLocaleString()}</strong>
              </div>
              <div className="preview-item">
                <span>Currency:</span>
                <strong>{currency}</strong>
              </div>
              {description && (
                <div className="preview-item">
                  <span>Description:</span>
                  <strong>{description}</strong>
                </div>
              )}
            </div>
            <div className="preview-list">
              <h4>Verified Recipients:</h4>
              <div className="preview-recipients">
                {payments.filter(p => p.status === 'verified').map((p, i) => (
                  <div key={i} className="preview-recipient">
                    <span>{p.recipient_name || p.recipient_contact}</span>
                    <span>{currency === 'USD' ? '$' : 'L$'}{p.amount.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="preview-actions">
              <button className="cancel-preview" onClick={() => setShowPreview(false)}>
                Cancel
              </button>
              <button className="confirm-payout" onClick={handleBulkPayout} disabled={processing}>
                {processing ? 'Processing...' : `Confirm & Send (${verifiedCount} payments)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {result && (
        <div className="payout-result">
          <div className="result-header">
            <h3>Payout Results</h3>
            <button onClick={() => setResult(null)} className="close-result">
              <FaTimes />
            </button>
          </div>
          <div className="result-summary">
            <div className="result-stat success">
              <FaCheckCircle />
              <span>Successful: {result.summary.total_successful}</span>
            </div>
            <div className="result-stat failed">
              <FaTimesCircle />
              <span>Failed: {result.summary.total_failed}</span>
            </div>
            <div className="result-stat total">
              <FaDollarSign />
              <span>Total: {result.summary.currency === 'USD' ? '$' : 'L$'}{result.summary.total_amount.toLocaleString()}</span>
            </div>
          </div>
          
          {result.failed_transactions.length > 0 && (
            <div className="failed-transactions">
              <h4>Failed Transactions:</h4>
              {result.failed_transactions.map((fail, i) => (
                <div key={i} className="failed-item">
                  <span>{fail.recipient_contact}</span>
                  <span>{result.summary.currency === 'USD' ? '$' : 'L$'}{fail.amount.toLocaleString()}</span>
                  <span className="error-msg">{fail.error}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BulkPayout;
